import React from 'react';
import { Link } from 'react-router-dom';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { getLocaleDateFormatTag } from '@/i18n/localeCatalog';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

function useFmtDate(locale) {
  return React.useCallback(
    (ts) => {
      if (!ts) return 'N/A';
      try {
        return new Date(Number(ts)).toLocaleString(getLocaleDateFormatTag(locale));
      } catch {
        return 'N/A';
      }
    },
    [locale],
  );
}

export default function ClassManagementPage({ session }) {
  const { t, locale } = useAppTranslation();
  const fmtDate = useFmtDate(locale);
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const isTeacher = session?.role === 'teacher' || session?.role === 'admin' || session?.role === 'super_admin';
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [result, setResult] = React.useState('');

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.listClasses(token);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(formatApiErrorMessage(e?.message, t) || t('errors.loadClassList'));
    } finally {
      setLoading(false);
    }
  }, [api, token, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <h1 className="font-headline text-3xl font-extrabold">{t('class.pageTitle')}</h1>
      {isTeacher ? (
        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-bold">{t('class.newTitle')}</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
              placeholder={t('class.namePlaceholder')}
            />
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
              placeholder={t('class.descPlaceholder')}
            />
          </div>
          <button
            type="button"
            onClick={async () => {
              if (!name.trim()) return;
              setResult('');
              try {
                const res = await api.createClass({ name, description }, token);
                setResult(t('class.createOk', { id: res.id }));
                setName('');
                setDescription('');
                await load();
              } catch (e) {
                setResult(formatApiErrorMessage(e?.message, t) || t('class.createFail'));
              }
            }}
            className="mt-3 rounded-xl bg-cyan-600 px-4 py-2 text-sm font-bold text-white"
          >
            {t('class.createBtn')}
          </button>
          {result ? <p className="mt-2 text-sm font-semibold text-emerald-700">{result}</p> : null}
        </section>
      ) : null}

      {loading ? <p className="text-sm text-slate-600">{t('class.loading')}</p> : null}
      {error ? <p className="text-sm font-semibold text-rose-700">{error}</p> : null}
      {!loading && items.length === 0 ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-5 text-sm text-slate-600 shadow-sm">
          {t('class.empty')}
        </div>
      ) : null}

      <div className="space-y-3">
        {items.map((row) => (
          <article key={row.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-bold text-slate-900">{row.name}</h2>
              <Link
                to={`/classes/${encodeURIComponent(row.id)}`}
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-cyan-500 hover:text-cyan-700"
              >
                <span className="material-symbols-outlined !text-base">open_in_new</span>
                {t('class.detail')}
              </Link>
            </div>
            <p className="mt-1 text-sm text-slate-600">{row.description || t('class.noDesc')}</p>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
              <span>
                {t('class.updated')}: {fmtDate(row.updated_at)}
              </span>
              {row.student_count != null ? (
                <span>
                  {t('class.students')}: {row.student_count}
                </span>
              ) : null}
              {row.quiz_count != null ? (
                <span>
                  {t('class.assignments')}: {row.quiz_count}
                </span>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
